import * as angular from 'angular';
import * as _ from 'lodash';
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import NotifyServiceName, {NotifyService} from "../../services/notify/notify.service";
import UserServiceName, {UserService} from "../../services/users/user.service";
import NewGuidServiceName, {NewGuidService} from "../../services/utility/newguid.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";
import AuthDataServiceName, {AuthDataService} from "../../services/auth/authData.service";
import UIUtilityServiceName, {UIUtilityService} from "../../services/utility/uiUtility.service";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {apsCommon} from "../../shared/dto/aps.common";
import template from './editUser.tpl.html';

let requires = [LoaderServiceName, NotifyServiceName, UserServiceName, NewGuidServiceName, NavigationServiceName,
  AuthDataServiceName, UIUtilityServiceName];

interface Plant extends apsCommon.IdAndName {
  selected: boolean;
}

export interface EditUserData {
  id: string;
  questions: apsViewModels.SecurityQuestionItem[];
  roles: apsViewModels.RoleListItem[];
  plants: apsCommon.IdAndName[];
}

export function openEditUser(data: EditUserData,
                             $modal: mm.foundation.modal): angular.IPromise<any> {
  return $modal.open({
    template: template,
    controller: 'EditUserCtrl as vm',
    resolve: {
      data: function() {
        return data;
      }
    }
  }).result;
}

export default angular.module('aps.controller.editUser', requires)
  .controller('EditUserCtrl', editUserCtrl)
  .name;

interface EditUserController {
  isSysAdminChanged(): void;
  isPlantAdminEnabled(plantId: string): boolean;
  isSysAdminEnabled(): boolean;
  close(): void;
  form: angular.IFormController;
  save(): void;
  deletedChanged(): void;
  nameChanged(): void;
  rolesInvalid(): boolean;
  removeRole(role: UserRole): void;
  addRole(): void;
  questions: apsViewModels.SecurityQuestionItem[];
  roles(): apsViewModels.RoleListItem[];
  user: any;
  selectedRole: apsViewModels.RoleListItem;
  plants: Plant[];
  isEdit: boolean;
}

interface UserRole {
  invalid(): string;
  removePlant(plant: apsCommon.IdAndName): void;
  addPlant(): void;
  selectedPlant: apsCommon.IdAndName;
  plants: apsCommon.IdAndName[];
  roleRequiresPlants: boolean;

  availablePlants(): apsCommon.IdAndName[];
}

function editUserCtrl(
  $modalInstance: mm.foundation.modalInstance,
  loaderService: LoaderService,
  notifyService: NotifyService,
  userService: UserService,
  newGuidService: NewGuidService,
  navigationService: NavigationService,
  authDataService: AuthDataService,
  data: EditUserData,
  uiUtilityService: UIUtilityService
) {
  let vm = this as EditUserController;

  vm.questions = data.questions;
  vm.roles = () => {
    if (!vm.user) {
      return data.roles;
    }

    let roles = [];
    _.forEach(data.roles, (role) => {
      if (_.find(vm.user.roles, {roleId: role.id})) {
        return;
      }
      roles.push(role);
    });
    return roles;
  };
  vm.selectedRole = _.first(vm.roles());

  navigationService.getNavigation().then( (nav) => {
    vm.plants = [];
    vm.isEdit = false;
    if (data.id) {
      loaderService.loadingPromise(
        userService.getUser(data.id)
          .then(user => {
            vm.user = user;
            vm.user.displayDefectsByNumber = vm.user.displayDefectsByNumber.toString();
            /* tslint:disable:no-use-before-declare */
            _.forEach(vm.user.roles, applyRoleFunctions);
            /* tslint:enable:no-use-before-declare */
            vm.isEdit = true;
            vm.selectedRole = _.first(vm.roles());
            _.forEach(nav.plants(), (p) => {
              vm.plants.push({ id: p.id, name: p.name, selected: _.includes(user.plantAdmins, p.id) });
            });
          }));
    } else {
      vm.user = {
        roles: [],
        isSysAdmin: false,
        plantAdmins: [],
        displayDefectsByNumber: true
      };
      _.forEach(nav.plants(), (p) => {
        vm.plants.push({ id: p.id, name: p.name, selected: false });
      });
      newGuidService.getNewGuid('user').then(id => { vm.user.id = id; });
    }
  });

  let applyRoleFunctions = (role: UserRole) => {
    role.availablePlants = () => {
      if (!role.roleRequiresPlants) {
        return [ ];
      }
      let plants = [];
      _.forEach(data.plants, (plant) => {
        if (_.find(role.plants, {id: plant.id})) {
          return;
        }
        plants.push(plant);
      });
      return plants;
    };
    role.selectedPlant = _.first(role.availablePlants());
    role.addPlant = () => {
      if (!role.selectedPlant) {
        return;
      }

      role.plants.push(role.selectedPlant);
      role.selectedPlant = _.first(role.availablePlants());
    };
    role.removePlant = (plant: apsCommon.IdAndName) => {
      _.remove(role.plants, plant);
      if (!role.selectedPlant) {
        role.selectedPlant = _.first(role.availablePlants());
      }
    };
    role.invalid = () => {
      if (role.roleRequiresPlants && role.plants.length === 0) {
        return 'A minimum of one plant is required for this role';
      }
      return undefined;
    };
  };

  vm.addRole = () => {
    if (!vm.selectedRole) {
      return;
    }

    let userRole: any = {
      roleId: vm.selectedRole.id,
      roleName: vm.selectedRole.name,
      roleRequiresPlants: vm.selectedRole.requiresPlants,
      roleLocation: vm.selectedRole.location,
      plants: []
    };

    applyRoleFunctions(userRole);

    if (userRole.roleRequiresPlants && userRole.availablePlants().length === 1) {
      userRole.addPlant();
    }

    vm.user.roles.push(userRole);
    vm.selectedRole = _.first(vm.roles());
  };

  vm.removeRole = (role: UserRole) => {
    _.remove(vm.user.roles, role);
    if (!vm.selectedRole) {
      vm.selectedRole = _.first(vm.roles());
    }
  };

  vm.rolesInvalid = () => {
    return vm.user.roles.length === 0;
  };

  vm.nameChanged = () => {
    if (vm.user.firstName && vm.user.lastName) {
      userService.getGeneratedUserName(
        vm.user.id, vm.user.firstName, vm.user.lastName
      ).then(userName => {
          vm.user.userName = userName;
        });
    } else {
      vm.user.userName = undefined;
    }
  };

  vm.deletedChanged = () => {
    if (!vm.user.isDeleted) {
      return;
    }

    uiUtilityService.confirm('Are you sure you want to disable this user?').then(confirmed => {
      vm.user.isDeleted = confirmed;
    });
  };

  vm.save = () => {
    if (vm.form.$invalid ||
      vm.rolesInvalid()) {
      return;
    }

    for (let i = 0; i < vm.user.roles.length; i++) {
      if (vm.user.roles[i].invalid()) {
        return;
      }
    }

    _.forEach(vm.user.roles, (role: any) => {
      role.plantIds = _.map(role.plants, (plant: any) => plant.id);
    });

    vm.user.plantAdmins = [];
    _.forEach(vm.plants, (p: any) => {
      if (p.selected) {
        vm.user.plantAdmins.push(p.id);
      }
    });

    if (vm.user.email === '') {
      vm.user.email = null;
    }

    let submit;
    if (vm.isEdit) {
      submit = userService.editUser(vm.user);
    } else {
      submit = userService.addUser(vm.user);
    }

    loaderService.loadingPromise(
    submit.then(function() {
        $modalInstance.close(vm.user);
      }, notifyService.handleApiErrors));
  };

  vm.close = () => {
    $modalInstance.close();
  };

  vm.isSysAdminEnabled = () => {
    return authDataService.isAdmin();
  };

  vm.isPlantAdminEnabled = (plantId) => {
    return !(vm.user && vm.user.isSysAdmin) && authDataService.isPlantAdmin(plantId);
  };

  vm.isSysAdminChanged = () => {
    if (vm.user && vm.user.isSysAdmin) {
      _.forEach(vm.plants, (p: any) => {
        p.selected = false;
      });
    }
  };
}



// WEBPACK FOOTER //
// ./src/app/controllers/users/editUser.controller.ts